import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import site from '../../content/siteContent';

export default function LocationLayout({ children }) {
  const { city } = useParams();
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    // Simulate data loading
    setLoading(true);
    setTimeout(() => setLoading(false), 500);
  }, [city]);

  const cityData = site.services[0].locations.find(
    (loc) => loc.toLowerCase() === city.toLowerCase()
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-sand/5 flex items-center justify-center">
        <div className="animate-pulse text-wood">Loading...</div>
      </div>
    );
  }

  if (!cityData) return (
    <div className="min-h-screen bg-sand/5 flex items-center justify-center">
      <div className="text-center">
        <h1 className="text-2xl font-bold text-wood mb-4">Location Not Found</h1>
        <p className="text-neutralText mb-6">The location you're looking for doesn't exist.</p>
        <Link to="/locations" className="btn btn-wood">View All Locations</Link>
      </div>
    </div>
  );

  const relevantServices = site.services.filter(
    (service) => service.locations.includes(cityData) || service.featuredLocation === cityData
  );

  const relevantProjects = site.projects.filter((project) => project.location === cityData);

  const relevantTestimonials = site.testimonials.filter(
    (testimonial) => testimonial.location === cityData
  );

  return (
    <div className="min-h-screen bg-sand/5">
      {/* Hero Section */}
      <div className="bg-wood text-sand py-16">
        <div className="container">
          <h1 className="text-4xl font-bold mb-4">Interior Design Services in {cityData}</h1>
          <p className="text-sand/90 max-w-2xl text-content">
            Premium interior solutions tailored for {cityData} homes and offices. Experience our
            end-to-end design and execution services.
          </p>
        </div>
      </div>

      <div className="container py-12">
        {/* Breadcrumb */}
        <div className="mb-8">
          <nav className="text-sm text-neutralText">
            <Link to="/" className="hover:text-wood">
              Home
            </Link>
            <span className="mx-2">/</span>
            <Link to="/locations" className="hover:text-wood">
              Locations
            </Link>
            <span className="mx-2">/</span>
            <span className="text-wood">{cityData}</span>
          </nav>
        </div>

        {/* Services */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Our Services in {cityData}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {relevantServices.map((service) => (
              <div key={service.title} className="card p-6">
                <h3 className="font-semibold mb-3">{service.title}</h3>
                <p className="text-sm text-neutralText">{service.description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Projects */}
        {relevantProjects.length > 0 && (
          <section className="mb-12">
            <h2 className="text-2xl font-semibold mb-6">Our Projects in {cityData}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {relevantProjects.map((project) => (
                <div key={project.id} className="card overflow-hidden">
                  <img
                    src={project.img}
                    alt={`${project.title} - Interior Design Project in ${cityData}`}
                    className="w-full h-48 object-cover"
                  />
                  <div className="p-4">
                    <h3 className="font-semibold">{project.title}</h3>
                    <p className="text-sm text-neutralText mt-1">{project.caption}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Testimonials */}
        {relevantTestimonials.length > 0 && (
          <section className="mb-12">
            <h2 className="text-2xl font-semibold mb-6">Client Testimonials from {cityData}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {relevantTestimonials.map((testimonial, idx) => (
                <div key={idx} className="card p-6">
                  <p className="text-neutralText italic mb-4">"{testimonial.text}"</p>
                  <div className="flex items-center">
                    <div className="flex-1">
                      <div className="font-semibold">{testimonial.name}</div>
                      <div className="text-sm text-neutralText">{testimonial.project}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Contact Section */}
        <section className="card p-8 text-center">
          <h2 className="text-2xl font-semibold mb-4">Start Your Project in {cityData}</h2>
          <p className="text-neutralText mb-6">
            Ready to transform your space? Get a free consultation and quote for your interior
            project in {cityData}.
          </p>
          <Link to="/contact" className="btn btn-wood">
            Contact Us
          </Link>
        </section>
      </div>
    </div>
  );
}
